import React, { useState } from "react";

//UI Material
import {
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
} from "@mui/material";

//Material Icons
import {
  ArrowForwardIos,
  Cancel,
  CheckBox,
  CheckBoxOutlineBlank,
} from "@mui/icons-material";

//Local components
import ContainedButton from "../basics/ContainedButton";
import ModalWindow from "../Modal";

//Helpers
import {
  typographyForCustomizedFontSize,
  typographyForSubtitles,
  typographyForText,
} from "../../Helpers/typography";
import { fetchWithTokenJSONPost } from "../../Helpers/network";

export default function TaskCard({ task, getTasks }) {
  // ==========>Component States<==========
  //extracted userData from local storage
  const userData = JSON.parse(localStorage.getItem("userData"));
  //State for cancel task loader
  const [cancellingLoader, setCancellingLoader] = useState(false);
  //State for complete task loader
  const [completingLoader, setCompletingLoader] = useState(false);
  //State for open modal
  const [modalOpen, setModalOpen] = useState(false);
  //State for modal text
  const [modalText, setModalText] = useState("");
  //State for modal description
  const [modalDescription, setModalDescription] = useState("");

  //Function to cancel the task
  const setCancelTask = async () => {
    setCancellingLoader(true);
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        "/tasks/set-canceled",
        { canceled: !task.canceled, taskNumber: task.taskNumber }
      );
      const jsonResponse = await requestResponse.json();

      if (jsonResponse.status === "ok") {
        getTasks();
      } else {
        setModalText("No se pudo realizar la acción.");
        setModalDescription(jsonResponse.description);
        setModalOpen(true);
      }
    } catch (error) {
      setModalText("No se pudo realizar la acción.");
      setModalDescription(
        "Error al hacer los cambios, por favor verifica tu conexión a internet e inténtalo nuevamente."
      );
      setModalOpen(true);
    }
    setCancellingLoader(false);
  };

  //Function to complete the task
  const setCompleteTask = async () => {
    setCompletingLoader(true);
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        "/tasks/set-completed",
        { completed: !task.completed, taskNumber: task.taskNumber }
      );
      const jsonResponse = await requestResponse.json();

      if (jsonResponse.status === "ok") {
        getTasks();
      } else {
        setModalText("No se pudo realizar la acción.");
        setModalDescription(jsonResponse.description);
        setModalOpen(true);
      }
    } catch (error) {
      setModalText("No se pudo realizar la acción.");
      setModalDescription(
        "Error al hacer los cambios, por favor verifica tu conexión a internet e inténtalo nuevamente."
      );
      setModalOpen(true);
    }
    setCompletingLoader(false);
  };

  //Function to display cancel icon button (ONLY ADMINS)
  const displayCancelButton = () => {
    if (userData.role === "ADMINISTRATOR") {
      if (task.canceled || task.completed) {
        return (
          <Grid item xs={12}>
            {typographyForCustomizedFontSize(
              `Asignada por ${task.createdBy}`,
              "11px"
            )}
          </Grid>
        );
      } else {
        return (
          <Grid item xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              {typographyForCustomizedFontSize(
                `Asignada por ${task.createdBy}`,
                "11px"
              )}
              <IconButton
                onClick={() => {
                  setCancelTask();
                }}
              >
                {cancellingLoader ? (
                  <CircularProgress size={30} />
                ) : (
                  <Cancel color="error" />
                )}
              </IconButton>
            </Stack>
          </Grid>
        );
      }
    } else {
      return (
        <Grid item xs={12}>
          {typographyForCustomizedFontSize(
            `Asignada por ${task.createdBy}`,
            "11px"
          )}
        </Grid>
      );
    }
  };

  //Function to select the button to complete/reactivate a task
  const displayTaskButton = () => {
    if (task.canceled) {
      if (userData.role === "ADMINISTRATOR") {
        return (
          <Stack direction="row" alignItems="center">
            <IconButton
              size="small"
              onClick={() => {
                setCancelTask();
              }}
            >
              {cancellingLoader ? (
                <CircularProgress size={30} />
              ) : (
                <Cancel color="error" />
              )}
            </IconButton>
            {typographyForText("Tarea cancelada")}
          </Stack>
        );
      } else {
        return (
          <Stack direction="row" alignItems="center">
            <Cancel color="error" />
            {typographyForText("Tarea cancelada")}
          </Stack>
        );
      }
    } else {
      return (
        <Stack direction="row" alignItems="center">
          {completingLoader ? (
            <CircularProgress size={30} />
          ) : (
            <IconButton
              size="small"
              onClick={() => {
                setCompleteTask();
              }}
            >
              {task.completed ? (
                <CheckBox color="success" />
              ) : (
                <CheckBoxOutlineBlank />
              )}
            </IconButton>
          )}
          {typographyForText("Tarea realizada")}
        </Stack>
      );
    }
  };

  // ==========>Component<==========
  return (
    <Card raised sx={{ height: "100%" }}>
      <Grid
        container
        sx={{ padding: "15px" }}
        spacing={0.5}
        justifyContent="flex-start"
        textAlign="left"
      >
        {displayCancelButton()}

        <Grid item xs={12}>
          <Stack>
            {typographyForSubtitles(task.title)}
            <Divider flexItem sx={{ marginTop: "4px", marginBottom: "4px" }} />
            {typographyForCustomizedFontSize(
              "Descripción",
              "11px",
              "#38bfc3",
              "BOLD"
            )}
            {typographyForText(task.description)}
            {typographyForCustomizedFontSize(
              "Fecha de creación",
              "11px",
              "#38bfc3",
              "BOLD"
            )}
            {typographyForText(new Date(task.createdAt).toLocaleDateString())}
            {typographyForCustomizedFontSize(
              "Finalizar antes de",
              "11px",
              "#38bfc3",
              "BOLD"
            )}
            {typographyForText(new Date(task.dueDate).toLocaleString())}
            {typographyForCustomizedFontSize(
              "Estado",
              "11px",
              "#38bfc3",
              "BOLD"
            )}
            {displayTaskButton()}
            {typographyForCustomizedFontSize(
              "Enlaces",
              "11px",
              "#38bfc3",
              "BOLD"
            )}
            <ContainedButton
              sx={{ marginTop: "5px" }}
              text={"Abrir enlace"}
              endIcon={<ArrowForwardIos />}
              variant={task.link === "" ? "disabled" : "contained"}
              onClick={() => {
                window.open(`${task.link}`);
              }}
            />
          </Stack>
        </Grid>
      </Grid>
      <ModalWindow
        isOpen={modalOpen}
        handleClose={() => setModalOpen(false)}
        title={modalText}
        text={modalDescription}
        leftText="OK"
        leftClick={() => {
          setModalOpen(false);
        }}
      />
    </Card>
  );
}
